import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { celebrateTaskCompleted } from './TaskCompletedCelebration';

const STATUS_CLASS = {
  'In Progress': 'in-progress',
  Completed: 'completed',
  'Not Started': 'not-started',
};

const STATUS_OPTIONS = [
  { label: 'In Progress' },
  { label: 'Completed' },
  { label: 'Not Started' },
];

/**
 * Task status pill / dropdown — shared by My Tasks and All Tasks.
 * Reuses `.project-my-tasks__status*` styles.
 */
const TaskStatusDropdown = ({ value, disabled = false, onChange }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const statusClass = STATUS_CLASS[value] || 'not-started';

  useEffect(() => {
    if (!open) return undefined;

    const close = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };

    const onScroll = () => setOpen(false);

    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const toggleMenu = () => {
    if (disabled || !onChange) return;
    if (open) {
      setOpen(false);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords({ top: rect.bottom + 4, left: rect.left });
    setOpen(true);
  };

  const statusEl = (
    <>
      <span className="project-my-tasks__status-dot" aria-hidden="true" />
      {value}
    </>
  );

  if (!onChange) {
    return (
      <span
        className={`project-my-tasks__status project-my-tasks__status--${statusClass}`}
      >
        {statusEl}
      </span>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`project-my-tasks__status project-my-tasks__status--${statusClass} project-my-tasks__status--dropdown`}
        onClick={toggleMenu}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Status: ${value}`}
      >
        {statusEl}
      </button>
      {open && coords
        ? createPortal(
            <ul
              ref={menuRef}
              className="project-my-tasks__status-menu"
              role="listbox"
              style={{ top: coords.top, left: coords.left }}
            >
              {STATUS_OPTIONS.map((option) => {
                const optionClass = STATUS_CLASS[option.label] || 'not-started';
                const isActive = option.label === value;
                return (
                  <li key={option.label} role="none">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`project-my-tasks__status-option project-my-tasks__status project-my-tasks__status--${optionClass} ${
                        isActive
                          ? 'project-my-tasks__status-option--active'
                          : ''
                      }`}
                      onClick={() => {
                        setOpen(false);
                        if (!isActive) {
                          if (option.label === 'Completed') {
                            celebrateTaskCompleted();
                          }
                          onChange(option.label);
                        }
                      }}
                    >
                      <span
                        className="project-my-tasks__status-dot"
                        aria-hidden="true"
                      />
                      {option.label}
                    </button>
                  </li>
                );
              })}
            </ul>,
            document.body
          )
        : null}
    </>
  );
};

export default TaskStatusDropdown;
export { STATUS_CLASS, STATUS_OPTIONS };
